/* eslint-disable @typescript-eslint/no-explicit-any */

import { getErrorMessage } from '@/shared/lib'
import {
    refreshTokenKeyword,
    saveToLocalStorage,
    sessionTokenLsKeyword,
} from '@/shared/localStorage/localStorageRepository'
// import { fetchBootstraps } from '@/streemiq/shared/lib/fetchBootstraps'
import { RequestMethod } from '@/api/apiClient'
import { post } from '@/api/httpRequests'
import { getBootstraps } from '@/clients/streamiq/shared/lib'

const baseUrl = process.env.REACT_APP_BASE_URL

export const createStreamIQApiService = () => {
    const loginRequest = async (
        requestMethod: RequestMethod,
        route: string,
        authToken?: string | null,
        includeBaseUrl?: boolean,
        requestBody?: string,
        requestHeaders?: object
    ) => {
        try {
            const requestData = createRequestData(
                requestMethod,
                route,
                undefined,
                requestBody,
                requestHeaders
            )

            const url =
                includeBaseUrl === undefined || includeBaseUrl
                    ? `${baseUrl}${route}`
                    : `${route}`

            const response = await fetch(`${url}`, requestData)

            if (!response?.ok) {
                return null
            }
            return await response.json()
        } catch (error) {
            getErrorMessage('request', error)
            throw error
        }
    }

    const signIn = async (userName: string, password: string) => {
        const bootstraps = getBootstraps()

        const url = bootstraps?.services?.['crm-service'].login

        if (!url) return

        try {
            const loginResponse = await loginRequest(
                'POST',
                url,
                null,
                false,
                JSON.stringify({
                    flow: 'password',
                    username: userName,
                    password,
                }),
                {
                    'Content-Type': 'application/json',
                    'X-Amz-Target':
                        'AWSCognitoIdentityProviderService.InitiateAuth',
                }
            )

            if (!loginResponse) {
                return null
            }

            saveToLocalStorage(sessionTokenLsKeyword, loginResponse.idToken)
            saveToLocalStorage(refreshTokenKeyword, loginResponse.refreshToken)

            return loginResponse.idToken
        } catch (error) {
            getErrorMessage('signIn', error)
            throw error
        }
    }

    const requestQRCode = async (): Promise<any> => {
        const bootstraps = getBootstraps()

        const url = bootstraps?.services?.['crm-service']?.logins
        const response = await post(
            url,
            false,
            JSON.stringify({
                flow: 'qrCode',
            })
        )
        return response.jsonData
    }

    const signInWithQRCode = async (id: string) => {
        const bootstraps = getBootstraps()

        const url = `${bootstraps?.services?.['crm-service']?.logins}/${id}`

        try {
            const loginResponse = await fetch(url, {
                method: 'get',
            })

            if (!loginResponse) {
                return null
            }
            const data = await loginResponse.json()

            saveToLocalStorage(sessionTokenLsKeyword, data.idToken)
            saveToLocalStorage(refreshTokenKeyword, data.refreshToken)
        } catch (error) {
            getErrorMessage('signIn', error)
            throw error
        }
    }

    const refreshAccessToken = async () => {
        const refreshToken = localStorage.getItem(refreshTokenKeyword)

        const bootstraps = getBootstraps()

        const url = bootstraps?.services?.['crm-service']?.login

        if (!refreshToken || !url) {
            getErrorMessage(
                'No refresh token available streamiq',
                'No refresh token available'
            )
            return null
        }
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Amz-Target':
                        'AWSCognitoIdentityProviderService.InitiateAuth',
                },
                body: JSON.stringify({
                    flow: 'refresh',
                    refreshToken,
                }),
            })
            if (!response.ok || response.status === 400) {
                getErrorMessage(
                    'refreshAccessToken stream iq',
                    'Refresh token is invalid or expired'
                )
                localStorage.removeItem(refreshTokenKeyword)
                localStorage.removeItem(sessionTokenLsKeyword)
                return null
            }

            const data = await response.json()

            saveToLocalStorage(sessionTokenLsKeyword, data.idToken)
            saveToLocalStorage(refreshTokenKeyword, data.refreshToken)

            return data.idToken
        } catch (error) {
            getErrorMessage('refreshAccessToken streaqm iq', error)
        }
    }

    const createRequestData = (
        requestMethod: RequestMethod,
        profileId?: string,
        authToken?: string | null,
        requestBody?: string,
        requestHeaders?: object
    ) => {
        const requestData = {
            method: requestMethod,
            body: requestBody ? requestBody : null,
            headers: authToken
                ? createAuthRequestHeaders(authToken, profileId)
                : createRequestHeaders(requestHeaders),
        }

        return requestData
    }

    const createAuthRequestHeaders = (
        authToken: string,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        profileId?: string
    ) => {
        const authRequestHeaders = {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
            Authorization: `Bearer ${authToken}`,
        }

        return authRequestHeaders
    }

    const createRequestHeaders = (headers?: object) => {
        const requestHeaders = {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
            ...headers,
        }

        return requestHeaders
    }

    const handleResetRequest = async (email: string) => {
        const bootstraps = getBootstraps()

        const url = bootstraps?.services?.['crm-service']?.commands

        try {
            await post(
                url,
                false,
                JSON.stringify({
                    command: 'resetPassword',
                    arguments: { username: email },
                })
            )
        } catch (error) {
            console.error(error)
        }
    }

    return {
        createRequestData,
        loginRequest,
        signIn,
        refreshAccessToken,
        signInWithQRCode,
        requestQRCode,
        handleResetRequest,
    }
}
